import React from 'react';

import { cacaClient } from '@plangrid-private/web-api-clients';

import { TOOL_IDS } from '@adsk/alloy-provider';
import { ClipboardIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import Modal from '@adsk/alloy-react-modal';
import { TabTitle } from '@adsk/exo-skeleton-react';

import FormTemplatesCustomDataView from './form-templates/FormTemplatesCustomDataView';
import FormTemplatesCustomPickerEmptyState from './form-templates/FormTemplatesCustomPickerEmptyState';
import FormTemplatesCustomPickerNoSearchResults from './form-templates/FormTemplatesCustomPickerNoSearchResults';
import FormTemplatesEntityBlock from './form-templates/FormTemplatesEntityBlock';
import FormTemplatesEntityMenuItem from './form-templates/FormTemplatesEntityMenuItem';
import FormsActivityCard from './FormsActivityCard';
import FormsCreateNewEntityPanel from './FormsCreateNewEntityPanel';
import FormsCustomCreateNewButton from './FormsCustomCreateNewButton';
import FormsCustomDataView from './FormsCustomDataView';
import FormsCustomPickerEmptyState from './FormsCustomPickerEmptyState';
import FormsCustomPickerNoSearchResults from './FormsCustomPickerNoSearchResults';
import FormsEntityBlock from './FormsEntityBlock';
import FormsEntityMenuItem from './FormsEntityMenuItem';
import { FormsProvider, formsFeatureFlags } from './FormsProvider';
import {
  FORM_PLUGIN_METADATA,
  FORM_FIELD_PLUGIN_METADATA,
  FORM_TEMPLATE_PLUGIN_METADATA,
  FORM_FILTERS_DEFAULT_VALUES,
  FORM_TEMPLATES_FILTERS_DEFAULT_VALUES,
  TREE_PAGE_SIZE,
  EMPTY_ROOT_FOLDER_ENTITY_ID,
  FEATURE_FLAGS,
} from './helpers/constants';
import { formsClient } from './helpers/formsClient';
import getFormsTransformer from './helpers/transformers/getForms';
import {
  parseFormFieldUrn,
  parseFormTemplateUrn,
  getFormTemplateUrn,
  fetchFormsByIds,
  fetchFormTemplatesByIds,
} from './helpers/utils';
/* eslint-disable no-unused-vars */

const sharedPluginConfig = {
  is3LOSupported: true,
  renderIcon: (p) => <ClipboardIcon {...p} />,
  toolId: TOOL_IDS.FORMS,
  EntityBlock: FormsEntityBlock,
  Provider: FormsProvider,
  ActivityCard: FormsActivityCard,
};

export const formTemplatePlugin = {
  ...sharedPluginConfig,

  metadata: {
    domain: FORM_TEMPLATE_PLUGIN_METADATA.DOMAIN,
    type: FORM_TEMPLATE_PLUGIN_METADATA.TYPE,
  },

  modalProps: { size: Modal.SIZES.MEDIUM },

  showSearchBox: true,

  PluralText: ({ lowerCase }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    const result = T.translate('FORM_TEMPLATES');
    if (lowerCase && typeof result === 'string') {
      return result.toLowerCase();
    }
    return result;
  },

  PickerModalTitle: ({ showTabTitle = true }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    const value = T.translate('ADD_FORM_TEMPLATE_REFERENCE');

    return (
      <>
        {showTabTitle && <TabTitle value={value} />}
        {value}
      </>
    );
  },

  AddRelationshipsButtonText: ({ count }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    return T.translate('SUBMIT');
  },

  SelectedCount: ({ count }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    return T.translate('FORM_TEMPLATES_SELECTED', { count });
  },

  queryEntities: ({ filters, projectId }) => {
    const { searchExpression, skip } = filters;

    if (filters.searchExpression) {
      return cacaClient.fieldReports
        .getFieldReportTypes(projectId, {
          ...FORM_TEMPLATES_FILTERS_DEFAULT_VALUES,
          limit: TREE_PAGE_SIZE,
          skip: skip || undefined,
          name: searchExpression,
        })
        .then(({ items, total }) => {
          const nextPageSkip = items.length + (skip || 0);
          const hasNextPage = total > nextPageSkip;

          return {
            data: items.map((template) => ({
              ...template,
              id: getFormTemplateUrn(template.uid),
            })),
            nextPageQuery: hasNextPage
              ? {
                  filters: { ...filters, skip: nextPageSkip },
                  projectId,
                }
              : undefined,
          };
        });
    } else {
      return cacaClient.fieldReports
        .getFieldReportTypes(projectId, {
          ...FORM_TEMPLATES_FILTERS_DEFAULT_VALUES,
          limit: TREE_PAGE_SIZE,
          folder_uids: ['null'],
        })
        .then(({ items }) => {
          if (!items || items.length === 0) {
            return {
              data: [{ id: EMPTY_ROOT_FOLDER_ENTITY_ID }],
            };
          }

          return {
            data: items.map((template) => ({
              ...template,
              id: getFormTemplateUrn(template.uid),
            })),
          };
        });
    }
  },

  getEntitiesByIds: ({ ids, projectId }) => {
    const templateUids = ids.map(parseFormTemplateUrn);

    return fetchFormTemplatesByIds(projectId, templateUids).then(
      (formTemplates) => ({
        data: formTemplates.map((template) => ({
          ...template,
          id: getFormTemplateUrn(template.uid),
        })),
      }),
    );
  },

  canCreate: ({ projectId }) => {
    return Promise.resolve(false);
  },

  CustomDataView: FormTemplatesCustomDataView,
  CustomPickerNoSearchResults: FormTemplatesCustomPickerNoSearchResults,
  CustomPickerEmptyState: FormTemplatesCustomPickerEmptyState,
  EntityBlock: FormTemplatesEntityBlock,
  EntityMenuItem: FormTemplatesEntityMenuItem,
  ActivityCard: null,
  CustomCreateNewButton: null,
  CreateNewEntityPanel: null,
};

export const formFieldsPlugin = {
  ...sharedPluginConfig,

  metadata: {
    domain: FORM_FIELD_PLUGIN_METADATA.DOMAIN,
    type: FORM_FIELD_PLUGIN_METADATA.TYPE,
  },

  PluralText: ({ lowerCase }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    const result = T.translate('FORM_QUESTIONS');
    if (lowerCase && typeof result === 'string') {
      return result.toLowerCase();
    }
    return result;
  },

  getEntitiesByIds: ({ ids, projectId }) => {
    const formIds = new Set();
    ids.forEach((id) => {
      const { formId } = parseFormFieldUrn(id);
      formIds.add(formId);
    });

    return fetchFormsByIds(projectId, Array.from(formIds), {
      include_pg_form_layout: true,
    }).then((forms) => {
      const formsMapByFormId = {};
      forms.forEach((form) => {
        formsMapByFormId[form.uid] = form;
      });

      const data = ids.reduce((acc, id) => {
        const { formId } = parseFormFieldUrn(id);
        return formsMapByFormId[formId]
          ? [
              ...acc,
              {
                ...formsMapByFormId[formId],
                id,
              },
            ]
          : acc;
      }, []);

      return { data };
    });
  },
};

const FormsPlugin = {
  ...sharedPluginConfig,

  metadata: {
    domain: FORM_PLUGIN_METADATA.DOMAIN,
    type: FORM_PLUGIN_METADATA.TYPE,
  },

  showSearchBox: true,

  PluralText: ({ lowerCase }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    const result = T.translate('FORMS');
    if (lowerCase && typeof result === 'string') {
      return result.toLowerCase();
    }
    return result;
  },

  PickerModalTitle: ({ showTabTitle = true }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    const value = T.translate('ADD_FORM_REFERENCE');

    return (
      <>
        {showTabTitle && <TabTitle value={value} />}
        {value}
      </>
    );
  },

  AddRelationshipsButtonText: ({ count }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    if (count > 1) {
      return T.translate('ADD_FORMS');
    }
    return T.translate('ADD_FORM');
  },

  SelectedCount: ({ count }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    if (count > 1) {
      return T.translate('FORMS_SELECTED', { count });
    }
    return T.translate('FORM_SELECTED', { count });
  },

  queryEntities: ({ filters, projectId }) => {
    const { searchExpression, skip } = filters;

    if (formsFeatureFlags[FEATURE_FLAGS.SCALING_FORMS_M7]) {
      return formsClient
        .getFormsScalingFormsM7(projectId, 'all', {
          ...FORM_FILTERS_DEFAULT_VALUES,
          text_search: searchExpression || undefined,
          skip: skip || undefined,
        })
        .then((forgeData) => {
          const reportsData = getFormsTransformer.translateResponse(forgeData);
          const nextPageSkip = reportsData.items.length + (skip || 0);
          const hasNextPage = reportsData.total > nextPageSkip;

          return {
            data: reportsData.items.map((item) => ({
              ...item,
              id: item.uid,
            })),
            nextPageQuery: hasNextPage
              ? {
                  filters: { ...filters, skip: nextPageSkip },
                  projectId,
                }
              : undefined,
          };
        });
    }
    return formsClient
      .getForms(projectId, 'all', {
        ...FORM_FILTERS_DEFAULT_VALUES,
        text_search: searchExpression || undefined,
        skip: skip || undefined,
      })
      .then((reportsData) => {
        const nextPageSkip = reportsData.items.length + (skip || 0);
        const hasNextPage = reportsData.total > nextPageSkip;

        return {
          data: reportsData.items.map((item) => ({
            ...item,
            id: item.uid,
          })),
          nextPageQuery: hasNextPage
            ? {
                filters: { ...filters, skip: nextPageSkip },
                projectId,
              }
            : undefined,
        };
      });
  },

  getEntitiesByIds: ({ ids, projectId }) => {
    return fetchFormsByIds(projectId, ids).then((forms) => ({
      data: forms.map((form) => ({
        ...form,
        id: form.uid,
      })),
    }));
  },

  canCreate: ({ projectId }) => {
    return Promise.resolve(true);
  },

  CustomPickerNoSearchResults: FormsCustomPickerNoSearchResults,
  CustomPickerEmptyState: FormsCustomPickerEmptyState,
  CustomCreateNewButton: FormsCustomCreateNewButton,
  CreateNewEntityPanel: FormsCreateNewEntityPanel,
  CustomDataView: FormsCustomDataView,
  EntityMenuItem: FormsEntityMenuItem,
};
/* eslint-enable no-unused-vars */

export default FormsPlugin;
